import type { AssetListItem } from '@cartwhl/common/api/getAssets';

import type { AssetLibraryBatchGroupAssetType, AssetLibraryGroupKind } from './assetLibraryGroupDetail';
import { getAssetLibraryGroupKind } from './assetLibraryGroupDetail';
import { resolveAssetBookmarkedState } from './assetLibraryGroupIds';
import type { AssetLibraryMediaPreviewState } from './assetLibraryMediaPreview';
import { toMediaPreviewFromAsset } from './assetLibraryMediaPreview';
import type { AssetLibraryView } from './assetLibraryView';
import type { AssetLibraryDisplayItem } from './utils';
import { getAssetCardTitle, shouldShowStackedPreview } from './utils';

export function getSceneMotionEditorPath(sceneID: string): string {
  return `/motion-editor/${encodeURIComponent(sceneID)}`;
}

export function isSceneAsset(asset: AssetLibraryDisplayItem): boolean {
  return asset.assetType === 'SCENE';
}

export type AssetLibraryClickAction =
  | { type: 'navigate-scene'; path: string }
  | { type: 'open-group-detail'; asset: AssetLibraryDisplayItem }
  | {
      type: 'open-media-preview';
      preview: Pick<
        AssetLibraryMediaPreviewState,
        | 'title'
        | 'mediaUrl'
        | 'mediaType'
        | 'createdAt'
        | 'createdBy'
        | 'isBookmarked'
        | 'showMainLibraryPreviewActions'
        | 'mediaSelection'
        | 'showOpenWithActions'
      >;
    }
  | {
      type: 'open-job-preview';
      jobId: string;
      groupID: string;
      assetType: AssetLibraryBatchGroupAssetType;
      groupKind: AssetLibraryGroupKind;
      isBookmarked: boolean;
      showMainLibraryPreviewActions: boolean;
    }
  | { type: 'select-mogen-job'; jobId: string; label: string }
  | { type: 'select-scene-motion'; sceneId: string; label: string }
  | { type: 'open-scene'; sceneId: string }
  | { type: 'select-stock-motion'; motionId: string; label: string }
  | { type: 'toggle-media-selection'; asset: AssetLibraryDisplayItem };

export type ResolveAssetLibraryClickActionOptions = {
  embedded?: boolean;
  libraryView?: AssetLibraryView;
  pickMode?: boolean;
  openSceneOnClick?: boolean;
  stagedMediaSelection?: boolean;
  showMainLibraryPreviewActions?: boolean;
  showOpenWithActions?: boolean;
  bookmarkReferenceAssets?: readonly AssetListItem[];
};

export function resolveAssetLibraryClickAction(
  asset: AssetLibraryDisplayItem,
  options: ResolveAssetLibraryClickActionOptions = {},
): AssetLibraryClickAction | null {
  if (options.libraryView === 'trash') {
    return null;
  }

  if (options.pickMode) {
    if (shouldShowStackedPreview(asset)) {
      return { type: 'open-group-detail', asset };
    }

    if (asset.assetType === 'MOGEN_JOB' || asset.assetType === 'MASCOT_JOB') {
      return {
        type: 'select-mogen-job',
        jobId: asset.assetID,
        label: getAssetCardTitle(asset),
      };
    }

    if (isSceneAsset(asset)) {
      if (options.openSceneOnClick) {
        return {
          type: 'open-scene',
          sceneId: asset.assetID,
        };
      }

      return {
        type: 'select-scene-motion',
        sceneId: asset.assetID,
        label: getAssetCardTitle(asset),
      };
    }

    if (
      options.stagedMediaSelection &&
      (asset.assetType === 'MEDIA_IMAGE' || asset.assetType === 'MEDIA_VIDEO')
    ) {
      return { type: 'toggle-media-selection', asset };
    }

    return null;
  }

  if (isSceneAsset(asset)) {
    return {
      type: 'navigate-scene',
      path: getSceneMotionEditorPath(asset.assetID),
    };
  }

  if (shouldShowStackedPreview(asset)) {
    return { type: 'open-group-detail', asset };
  }

  if (
    (asset.assetType === 'MOGEN_JOB' || asset.assetType === 'MASCOT_JOB') &&
    asset.groupID
  ) {
    const bookmarkReferenceAssets = options.bookmarkReferenceAssets ?? [];

    return {
      type: 'open-job-preview',
      jobId: asset.assetID,
      groupID: asset.groupID,
      assetType: asset.assetType as AssetLibraryBatchGroupAssetType,
      groupKind: getAssetLibraryGroupKind(asset),
      isBookmarked: resolveAssetBookmarkedState(
        asset,
        bookmarkReferenceAssets,
      ),
      showMainLibraryPreviewActions: Boolean(
        options.showMainLibraryPreviewActions,
      ),
    };
  }

  const mediaPreview = toMediaPreviewFromAsset(asset);
  if (mediaPreview) {
    return {
      type: 'open-media-preview',
      preview: {
        ...mediaPreview,
        showMainLibraryPreviewActions: Boolean(
          options.showMainLibraryPreviewActions,
        ),
        showOpenWithActions: Boolean(options.showOpenWithActions),
      },
    };
  }

  return null;
}

export type AssetLibraryClickHandlers = {
  navigate: (path: string) => void;
  openGroupDetail: (asset: AssetLibraryDisplayItem) => void;
  openMediaPreview: (
    preview: Pick<
      AssetLibraryMediaPreviewState,
      | 'title'
      | 'mediaUrl'
      | 'mediaType'
      | 'createdAt'
      | 'createdBy'
      | 'isBookmarked'
      | 'showMainLibraryPreviewActions'
      | 'mediaSelection'
      | 'showOpenWithActions'
    >,
  ) => void;
  openJobPreview: (input: {
    jobId: string;
    groupID: string;
    assetType: AssetLibraryBatchGroupAssetType;
    groupKind: AssetLibraryGroupKind;
    isBookmarked?: boolean;
    showMainLibraryPreviewActions?: boolean;
  }) => void;
  selectMogenJob: (input: { jobId: string; label: string }) => void;
  selectSceneMotion: (input: { sceneId: string; label: string }) => void;
  openScene: (input: { sceneId: string }) => void;
  selectStockMotion: (input: { motionId: string; label: string }) => void;
  toggleMediaSelection: (asset: AssetLibraryDisplayItem) => void;
};

export function applyAssetLibraryClickAction(
  action: AssetLibraryClickAction,
  handlers: AssetLibraryClickHandlers,
) {
  switch (action.type) {
    case 'navigate-scene':
      handlers.navigate(action.path);
      break;
    case 'open-group-detail':
      handlers.openGroupDetail(action.asset);
      break;
    case 'open-media-preview':
      handlers.openMediaPreview(action.preview);
      break;
    case 'open-job-preview':
      handlers.openJobPreview(action);
      break;
    case 'select-mogen-job':
      handlers.selectMogenJob(action);
      break;
    case 'select-scene-motion':
      handlers.selectSceneMotion(action);
      break;
    case 'open-scene':
      handlers.openScene(action);
      break;
    case 'select-stock-motion':
      handlers.selectStockMotion(action);
      break;
    case 'toggle-media-selection':
      handlers.toggleMediaSelection(action.asset);
      break;
  }
}
